import { persistQueryClientSave } from '@tanstack/react-query-persist-client';

const PERSISTABLE_KEYS = new Set([
    'loggedInUser', 'organizationEntitlements', 'organizations', 'upcomingTasks',
    'upcomingTaskAssignments', 'smallGroups', 'shortcuts', 'upcomingTrainingClasses',
    'dashboardSections', 'dashboardData', 'registrations', 'smallGroupTypes',
]);
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const CACHE_EVENTS = new Set(['added', 'removed', 'updated']);
const FETCH_ACTIONS = new Set(['fetch', 'failed', 'pause', 'continue']);

export const shouldPersistQuery = query => {
    // Preserve the existing offline allowlist, excluding infinite pages and
    // group-detail snapshots that can change outside this app.
    if (query.state.status !== 'success' || Array.isArray(query.state.data?.pages)) return false;
    const key = query.queryKey;
    if (key[0] === 'smallGroups' && UUID.test(key[1])) return false;
    return PERSISTABLE_KEYS.has(key[0]);
};

// Order entire persister operations, not just storage.setItem: serialization
// itself may yield. Removal invalidates queued snapshots and late restores.
export const createOrderedPersister = persister => {
    let generation = 0;
    let tail = Promise.resolve();
    const enqueue = operation => {
        const result = tail.then(operation);
        tail = result.catch(() => {});
        return result;
    };
    return {
        getGeneration: () => generation,
        persistClient: snapshot => {
            const expected = generation;
            return enqueue(() => expected === generation ? persister.persistClient(snapshot) : undefined);
        },
        restoreClient: () => {
            const expected = generation;
            return enqueue(async () => {
                const snapshot = await persister.restoreClient();
                return expected === generation ? snapshot : undefined;
            });
        },
        removeClient: () => {
            generation += 1;
            return enqueue(() => persister.removeClient());
        },
    };
};

export const createBatchedPersistence = (options, { delay = 1000, onError = () => {} } = {}) => {
    const { queryClient, persister, dehydrateOptions } = options;
    const includeQuery = dehydrateOptions?.shouldDehydrateQuery || shouldPersistQuery;
    const includeMutation = dehydrateOptions?.shouldDehydrateMutation || (mutation => mutation.state.isPaused);
    const saveOptions = {
        ...options,
        dehydrateOptions: { shouldDehydrateQuery: shouldPersistQuery, ...dehydrateOptions },
    };
    const queries = new Set(queryClient.getQueryCache().getAll().filter(includeQuery).map(query => query.queryHash));
    const mutations = new Set(queryClient.getMutationCache().getAll().filter(includeMutation));
    const generation = persister.getGeneration();
    let live = true;
    let dirty = false;
    let timer = null;
    let running = null;
    const current = () => live && generation === persister.getGeneration();
    const cancelTimer = () => {
        if (timer !== null) clearTimeout(timer);
        timer = null;
    };
    const schedule = () => {
        if (!current()) return;
        dirty = true;
        // Fixed window: continuous updates cannot postpone a save forever.
        if (timer === null && !running) timer = setTimeout(() => { void flush(); }, delay);
    };
    const flush = () => {
        cancelTimer();
        if (!current() || !dirty) return running || Promise.resolve();
        if (running) return running.then(flush);
        dirty = false;
        running = Promise.resolve()
            .then(() => current() ? persistQueryClientSave(saveOptions) : undefined)
            .catch(onError)
            .finally(() => {
                running = null;
                if (dirty && current()) schedule();
            });
        return running;
    };
    const unsubscribeQueries = queryClient.getQueryCache().subscribe(event => {
        if (!current() || !CACHE_EVENTS.has(event.type)) return;
        if (event.type === 'updated' && FETCH_ACTIONS.has(event.action.type)) return;
        const { query } = event;
        const included = event.type !== 'removed' && includeQuery(query);
        const previous = queries.has(query.queryHash);
        if (included) queries.add(query.queryHash);
        else queries.delete(query.queryHash);
        if (included || previous) schedule();
    });
    const unsubscribeMutations = queryClient.getMutationCache().subscribe(event => {
        if (!current() || !CACHE_EVENTS.has(event.type)) return;
        const included = event.type !== 'removed' && includeMutation(event.mutation);
        const previous = mutations.has(event.mutation);
        if (included) mutations.add(event.mutation);
        else mutations.delete(event.mutation);
        if (included || previous) schedule();
    });
    return {
        flush,
        stop: () => {
            live = false;
            dirty = false;
            cancelTimer();
            unsubscribeQueries();
            unsubscribeMutations();
        },
    };
};
